import React, { useEffect, useState } from 'react'
import {
  Box,
  Flex,
  PrimaryButton,
  Text,
  Image,
  Heading,
  IconButton,
  LoadingIndicator,
} from '@moonpig/launchpad-components'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { spacingPx, theme } from '@moonpig/launchpad-theme'
import { IconSystemBin, IconSystemChevronUp } from '@moonpig/launchpad-assets'
import {
  useFetchMultiBuyList,
  Status,
  useRemoveFromMultiBuyList,
} from '@moonpig/web-shared-products'
import { useFindLocaleText } from '../../text-localisation'
import { useStoreId } from '@moonpig/web-core-stores'
import { EmptyState } from './components/EmptyState'

const PANEL_MAX_WIDTH = '600px'
const PANEL_CORNER_RADIUS = '16px'

const StyledCounter = styled.div`
  border-radius: 50%;
  ${s({
    bgcolor: 'colorBackgroundInformation',
    width: 24,
    height: 24,
    color: 'colorInteractionSelectedState',
    typography: 'typeBodyText',
    ml: 4,
  })}
`

const StyledFixedWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  pointer-events: none;
`

const StyledPanel = styled(Box)<{ $isExpanded: boolean }>`
  pointer-events: auto;
  cursor: default;  
  ${({ $isExpanded }) =>
    s({
      bgcolor: 'colorBackground01',
      boxShadow: theme.shadows.boxShadow02,
      maxWidth: { md: PANEL_MAX_WIDTH },
      marginLeft: 'auto',
      px: 6,
      pb: $isExpanded ? 6 : 0,
    })}
  
  ${breakpointUp('md')} {
    margin-right: 24px;
  }

  border-top-left-radius: ${PANEL_CORNER_RADIUS};
  border-top-right-radius: ${PANEL_CORNER_RADIUS};
`

const StyledHeader = styled.button`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    ${s({ py: 5 })}
`

const StyledChevron = styled(IconSystemChevronUp)`
    flex-shrink: 0;
    transition: transform 200ms ease-in-out;
    ${s({
      color: 'colorInteractionIcon',
      width: 28,
      height: 28,
    })};

    &.collapsed {
        transform: rotate(180deg);
    }
`

const StyledItems = styled(Flex)`
  align-items: center;
  overflow-x: auto;
  gap: ${spacingPx(5)};
  ${s({
    padding: 6,
    marginBottom: 6,
    borderColor: 'colorBorder03',
    border: '2px dashed',
  })}
`

const StyledItemImage = styled(Image)`
  ${s({
    boxShadow: theme.shadows.boxShadow01,
    width: '100%',
    height: '100%',
  })}
`

const StyledContentWidth = styled.div`
  pointer-events: none;
  ${({ theme: { siteWidth } }) => s({ maxWidth: siteWidth, mx: 'auto' })}
`

const StyledItemWrapper = styled(Box)`
  position: relative;
  flex: 0 0 auto;
`
const StyledDeleteButton = styled(IconButton)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  z-index: 1;
  ${s({
    width: 28,
    height: 28,
    minHeight: 28,
    minWidth: 28,
    bottom: -6,
    bgcolor: 'colorBackground01',
    color: 'colorFeedbackError',
    boxShadow: theme.shadows.boxShadow01,
  })}

  svg {
    ${s({
      width: 16,
      height: 16,
    })}
  }
`

const handlePersonalise = (
  region: string,
  productKey: string,
  variantKey: string,
) => {
  const url = `/${region}/customise/product/${productKey}/${variantKey}/`
  window.location.assign(url)
}

export const ShoppingList = () => {
  const localiseText = useFindLocaleText()
  const [isExpanded, setIsExpanded] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isMissingFields, setIsMissingFields] = useState(false)
  const [itemsToDelete, setItemsToDelete] = useState(new Set<string>())
  const region = useStoreId()

  const { fetchMultiBuyList, multiBuyListItems, multiBuyListItemCount } =
    useFetchMultiBuyList()

  const { removeFromMultiBuyList } = useRemoveFromMultiBuyList()

  const productIdForFirstItemInList = multiBuyListItems[0]?.variant.productId

  const variantKeyForFirstItemInList = multiBuyListItems[0]?.variant.key

  useEffect(() => {
    fetchMultiBuyList()
  }, [fetchMultiBuyList])

  const DeleteLoadingIcon = () => (
    <LoadingIndicator label={'Removing'} size={8} color="colorFeedbackError" />
  )

  const handleOnRemove = async (id: string) => {
    setErrorMessage(null)
    setItemsToDelete(prev => new Set(prev).add(id))

    const response = await removeFromMultiBuyList(id)

    setItemsToDelete(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

    if (response.error?.errorMessage) {
      setErrorMessage(response.error.errorMessage)
    }
  }

  const toggleExpanded = () => setIsExpanded(prev => !prev)

  const reversedMultiBuyItems = [...multiBuyListItems].reverse()

  const isListEmpty = multiBuyListItemCount === 0

  return (
    <>
      <Status
        error={{
          status: !!errorMessage || isMissingFields,
          message:
            errorMessage ??
            (isMissingFields
              ? localiseText('find.something_went_wrong')
              : undefined),
          onDismiss: () => {
            setErrorMessage(null)
            setIsMissingFields(false)
          },
        }}
      />
      <StyledFixedWrapper>
        <StyledContentWidth>
          <StyledPanel
            role="region"
            aria-label={localiseText('find.shopping_list.title')}
            data-testid="shopping-list"
            $isExpanded={isExpanded}
          >
            <StyledHeader
              type="button"
              data-testid="shopping-list-header"
              onClick={toggleExpanded}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? localiseText('find.shopping_list_collapse')
                  : localiseText('find.shopping_list_expand')
              }
            >
              <Flex alignItems="center">
                <Heading
                  level="h3"
                  typography="typeDisplay05"
                  color="colorTextHeadline"
                  margin="0"
                >
                  {localiseText('find.shopping_list.title')}
                </Heading>
                {!isListEmpty && (
                  <StyledCounter data-testid="counter">
                    <Text fontWeight="bold">{multiBuyListItemCount}</Text>
                  </StyledCounter>
                )}
              </Flex>
              <StyledChevron
                className={isExpanded ? '' : 'collapsed'}
                aria-hidden
              />
            </StyledHeader>
            {isExpanded && (
              <>
                <StyledItems>
                  {reversedMultiBuyItems.map((item, index) => (
                    <StyledItemWrapper
                      width={{ xs: '56px', md: '70px' }}
                      minWidth={{ xs: '56px', md: '70px' }}
                      key={item.id}
                    >
                      <StyledItemImage
                        src={item.variant.productImages.mainImage.small.jpgUrl}
                        alt={item.variant.sku || ''}
                      />
                      <StyledDeleteButton
                        icon={
                          itemsToDelete.has(item.id)
                            ? DeleteLoadingIcon
                            : IconSystemBin
                        }
                        label={localiseText('find.shopping_list_remove_item', {
                          cardPosition: index + 1,
                        })}
                        onClick={e => {
                          e.stopPropagation()
                          handleOnRemove(item.id)
                        }}
                      />
                    </StyledItemWrapper>
                  ))}
                  {isListEmpty && <EmptyState />}
                </StyledItems>
                {!isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    onClick={() => {
                      if (
                        !productIdForFirstItemInList ||
                        !variantKeyForFirstItemInList
                      ) {
                        setIsMissingFields(true)
                        return
                      }

                      handlePersonalise(
                        region,
                        productIdForFirstItemInList,
                        variantKeyForFirstItemInList,
                      )
                    }}
                  >
                    {localiseText('find.shopping_list.cta.personalise')}
                  </PrimaryButton>
                )}
                {isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    onClick={() => {
                      toggleExpanded()
                    }}
                  >
                    {localiseText('find.shopping_list.cta.continue_browsing')}
                  </PrimaryButton>
                )}
              </>
            )}
          </StyledPanel>
        </StyledContentWidth>
      </StyledFixedWrapper>
    </>
  )
}
